import { useState, useEffect, useMemo, useRef } from 'react';
import { VideoUtilsWorkerClient } from "../Utils/videoUtils/videoUtils.client";
import { VideoReadyResult } from "../Utils/videoUtils/videoUtils";
import { RegionScroller, RegionWindowSizeChangeProps } from './RegionScroller';
import { ClipWindowContext } from '../ClipMaker/ClipWindowContext';
import { ClipZoom, ClipZoomResult } from '../ClipMaker/ClipZoom';
import { ClipPreview } from '../ClipMaker/ClipPreview';
import { VideoThumbnailBar } from './VideoThumbnailBar';
import { formatTime } from '../Utils/ConversionUtils'
import './css/VideoClipMaker.css';
import { ClipInterval } from '../ClipMaker/types/ClipInterval';

interface VideoClipMakerProps {
    videoBuffer: ArrayBuffer | null;
    videoName: string | null;
    title: string;
    initialRequiredFrames?: number;
    zoomSteps?: number;
    maxClipWindowFactor?: number;
    clipSelected?: (result: VideoClipSelectedResult) => void
}

export interface VideoClipSelectedResult {
    startSecs: number;
    durationSecs: number;
    frameRate: number;
}

const MIN_CLIP_SCREEN_PIXEL_WIDTH = 32;
const MAX_CLIP_DURATION_SECS = 10;
const MIN_CLIP_DURATION_FRAMES = 80;

export const VideoClipMaker: React.FC<VideoClipMakerProps> = ({
    videoBuffer,
    videoName,
    title,
    initialRequiredFrames = MIN_CLIP_DURATION_FRAMES,
    zoomSteps = 20,
    maxClipWindowFactor = 0.75,
    clipSelected
}) => {

    const extractor = useMemo(() => new VideoUtilsWorkerClient(), []);

    const [zoomIndex, setZoomIndex] = useState(zoomSteps / 2); // initial neutral zoom
    const [videoMetadata, setVideoMetadata] = useState<VideoReadyResult | null>(null);
    const pixelsPerSecondRef = useRef(0);
    const [pixelsPerSecond, setPixelsPerSecond] = useState(0);
    const [scrollWindowWidth, setScrollWindowWidth] = useState(0);
    const [minScrollWindowWidth, setMinScrollWindowWidth] = useState(0);
    const [maxScrollWindowWidth, setMaxScrollWindowWidth] = useState(0);
    const [clipStartSecs, setClipStartSecs] = useState(0);
    const clipStartSecsRef = useRef(0);
    const [windowStartSecs, setWindowStartSecs] = useState(0);
    const [clipDurationSecs, setClipDurationSecs] = useState(0);
    const clipDurationSecsRef = useRef(0);
    const [regionTitle, setRegionTitle] = useState<string | null>(null);
    const [regionWindowWidthPx, setRegionWindowWidthPx] = useState(0);
    const [regionWindowLeftPx, setRegionWindowLeftPx] = useState(0);
    const [scrollWindowLeftPx, setScrollWindowLeftPx] = useState(0);

    const [videoDuration, setVideoDuration] = useState<number>(0);
    const [currentClipInterval, setCurrentClipInterval] = useState<ClipInterval | null>(null);
    const [selectedClipInterval, setSelectedClipInterval] = useState<ClipInterval | null>(null);
    const [viewportInterval, setViewportInterval] = useState<ClipInterval | null>(null);

    useEffect(() => {
        if (videoBuffer) {
            extractor.props(videoBuffer, async (result: VideoReadyResult) => {
                setVideoMetadata(result);
            });
        }
    }, [videoBuffer]);

    useEffect(() => {
        pixelsPerSecondRef.current = pixelsPerSecond;
        clipDurationSecsRef.current = clipDurationSecs;
        clipStartSecsRef.current = clipStartSecs;
    }, [pixelsPerSecond, clipDurationSecs, clipStartSecs]);

    useEffect(() => {
        if (videoMetadata && zoomIndex && scrollWindowWidth) {
            const T_clip = initialRequiredFrames / videoMetadata.frameRate;

            // Zoom range
            const S_min = MIN_CLIP_SCREEN_PIXEL_WIDTH / T_clip;
            const S_max = (maxClipWindowFactor * scrollWindowWidth) / T_clip;

            const deltaS = (S_max - S_min) / zoomSteps;

            const pps = S_min + zoomIndex * deltaS;
            setPixelsPerSecond(pps);

            setMaxScrollWindowWidth(MAX_CLIP_DURATION_SECS * pps);

            const minClipDuration = MIN_CLIP_DURATION_FRAMES / videoMetadata.frameRate;

            setMinScrollWindowWidth(minClipDuration * pps);

            const newClipDuration = clipDurationSecs ? clipDurationSecs : minClipDuration;

            setClipDurationSecs(newClipDuration); // only set to min clip duration on mount
            setRegionWindowLeftPx(clipStartSecs * pps);
            setScrollWindowLeftPx(windowStartSecs * pps);
            setRegionTitle(formatTime(clipStartSecs));

            setVideoDuration(videoMetadata.durationSecs);

            setSelectedClipInterval({ start: clipStartSecs, duration: newClipDuration });

            clipSelected?.({ startSecs: clipStartSecs, durationSecs: newClipDuration, frameRate: videoMetadata.frameRate });
        }
    }, [zoomIndex, videoMetadata, scrollWindowWidth]);

    useEffect(() => {
        if (clipDurationSecs && videoMetadata && pixelsPerSecond) {
            setRegionWindowWidthPx(clipDurationSecs * pixelsPerSecond);
            setRegionWindowLeftPx(clipStartSecs * pixelsPerSecond);
        }
    }, [clipDurationSecs, videoMetadata, pixelsPerSecond, clipStartSecs]);

    useEffect(() => {
        if (videoDuration && scrollWindowWidth && clipDurationSecs) {
            const scrollWindowWidthDuration = scrollWindowWidth / pixelsPerSecondRef.current;

            setCurrentClipInterval({ start: clipStartSecs, duration: clipDurationSecs });
            setViewportInterval({ start: windowStartSecs, duration: scrollWindowWidthDuration });
        }
    }, [videoDuration, scrollWindowWidth, windowStartSecs, clipStartSecs, clipDurationSecs])

    function calculatedPixelsPerSecond(leftPx: number, scrollOffsetPx: number) {
        const newLeftPx = leftPx + scrollOffsetPx;
        return newLeftPx / pixelsPerSecondRef.current;
    }

    const regionWindowSizeChangeHandler = (e: RegionWindowSizeChangeProps) => {
        const newClipDurationSecs = e.widthPx / pixelsPerSecondRef.current;
        setClipDurationSecs(newClipDurationSecs);
    }

    const regionWindowSizeChangedHandler = (e: RegionWindowSizeChangeProps) => {
        const newClipDurationSecs = e.widthPx / pixelsPerSecondRef.current;
        setClipDurationSecs(newClipDurationSecs);
        setSelectedClipInterval({ start: clipStartSecsRef.current, duration: newClipDurationSecs });

        const frameRate = videoMetadata ? videoMetadata.frameRate : 0;

        clipSelected?.({ startSecs: clipStartSecsRef.current, durationSecs: newClipDurationSecs, frameRate: frameRate });
    }

    const onZoomChange = (result: ClipZoomResult) => {
        setZoomIndex(Math.max(0, Math.min(result.newValue, zoomSteps)));
    };

    const clipWindowContextViewportChange = (viewport: ClipInterval, clip: ClipInterval) => {
        setRegionWindowLeftPx((viewport.start - clip.start) * pixelsPerSecondRef.current);
        setScrollWindowLeftPx(viewport.start * pixelsPerSecondRef.current);

        setSelectedClipInterval({ start: clip.start, duration: clip.duration });
        setCurrentClipInterval({ start: clip.start, duration: clip.duration });
        setViewportInterval({ start: viewport.start, duration: viewport.duration });
    }

    return (
        <div className="video-clip-maker-container">
            <div className="video-clip-maker-title">
                <span>{title}</span>
            </div>
            <div className="video-clip-maker-preview-container clip-maker-row-section fill settle-effect">
                {selectedClipInterval && videoDuration && videoMetadata && videoBuffer &&
                    <ClipPreview video={videoBuffer}
                        aspectRatio={videoMetadata.aspectRatio}
                        startSecs={selectedClipInterval?.start}
                        durationSecs={selectedClipInterval?.duration} />
                }
            </div>

            {viewportInterval && videoDuration &&
                <div className="video-clip-maker-context-container clip-maker-row-section fit settle-effect">
                    {currentClipInterval &&
                        <ClipWindowContext clip={currentClipInterval}
                            viewport={viewportInterval}
                            videoDurationSecs={videoDuration}
                            onViewportChange={clipWindowContextViewportChange} />
                    }
                </div>
            }

            <div className="region-scroller-container-outer clip-maker-row-section fit settle-effect">
                {
                    videoBuffer && videoName && videoMetadata && <>
                        <RegionScroller
                            regionWidthPx={regionWindowWidthPx}
                            regionMaxWidthPx={maxScrollWindowWidth}
                            regionMinWidthPx={minScrollWindowWidth}
                            initialRegionWindowLeftPx={regionWindowLeftPx}
                            initialScrollLeftPx={scrollWindowLeftPx}
                            regionWindowPositionTitle={regionTitle}
                            regionWindowInteriorTitle={formatTime(clipDurationSecs)}
                            onWidthChange={(width) => {
                                setScrollWindowWidth(width);
                            }}
                            regionWindowChanged={(args) => {
                                setRegionTitle(formatTime(calculatedPixelsPerSecond(args.leftPx, args.scrollOffsetPx)));

                                const csSecs = calculatedPixelsPerSecond(args.leftPx, args.scrollOffsetPx);
                                setClipStartSecs(csSecs);
                                setWindowStartSecs(calculatedPixelsPerSecond(0, args.scrollOffsetPx))

                                setSelectedClipInterval({ start: csSecs, duration: clipDurationSecsRef.current });

                                const frameRate = videoMetadata ? videoMetadata.frameRate : 0;

                                clipSelected?.({ startSecs: csSecs, durationSecs: clipDurationSecsRef.current, frameRate: frameRate });
                            }}
                            regionWindowChange={(args) => {
                                setRegionTitle(formatTime(calculatedPixelsPerSecond(args.leftPx, args.scrollOffsetPx)));

                                setClipStartSecs(calculatedPixelsPerSecond(args.leftPx, args.scrollOffsetPx));
                                setWindowStartSecs(calculatedPixelsPerSecond(0, args.scrollOffsetPx))
                            }}
                            regionWindowSizeChange={regionWindowSizeChangeHandler}
                            regionWindowSizeChanged={regionWindowSizeChangedHandler}>
                            <VideoThumbnailBar video={videoBuffer}
                                videoMetadata={videoMetadata}
                                filename={videoName!}
                                pixelsPerSecond={pixelsPerSecond} />
                        </RegionScroller>
                        <ClipZoom zoomValue={zoomIndex} onChange={onZoomChange} />
                    </>
                }
            </div>
        </div>
    );
};
